/*
Example exchange rate chart that extends a line chart
*/
import * as d3 from "d3";
import {
  TimeSeriesChart,
  percentChange,
  quarter,
  placeTooltip,
} from "@aodin/charts";

function parseRateArray(d) {
  // x, z, y
  return {
    x: d3.isoParse(d[0]),
    y: d[2],
    z: d[1],
  };
}

class RatesChart extends TimeSeriesChart {
  yFormat = d3.format("+,~%");

  constructor(data) {
    super(data);
    this.screenHeightPercent(0.75);
    this.yAxisRight();
    this.useContinuousScheme(d3.interpolateRainbow, 0.1, 0.9);
  }

  parseData(data) {
    return d3.map(data.values, parseRateArray);
  }

  parseItems(data) {
    return data.items;
  }

  parseZ(data) {
    return d3.map(this.items, (d) => d.abbrev);
  }

  get legend() {
    // Return the z items along with their colors
    return d3.map(this.items, (d) => {
      return Object.assign({ color: this.colors(d.abbrev) }, d);
    });
  }

  render(selector) {
    super.render(selector);

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "chart-tooltip")
      .style("pointer-events", "none")
      .style("position", "absolute")
      .style("visibility", "hidden");

    const names = this.legend.reduce((acc, o) => {
      acc[o.abbrev] = o.name;
      return acc;
    }, {});

    const onMove = (d, evt) => {
      this.highlight(d.z);

      // Set text of tooltip, then set coordinates
      const name = names[d.z];
      const q = quarter(d.x);
      if (d.y) {
        const percent = percentChange(d.y);
        tooltip.html(
          `<strong>${name}</strong></br><em>${q}</em></br>${percent}`,
        );
      } else {
        tooltip.html(`<strong>${name}</strong></br><em>${q}</em>`);
      }

      const pad = evt.touchType ? 20 : 5;
      const [tx, ty, align] = placeTooltip(
        this.svg.node(),
        tooltip.node(),
        d.dx,
        d.dy,
        pad,
      );

      tooltip
        .style("visibility", null)
        .style("text-align", align)
        .style("left", `${tx}px`)
        .style("top", `${ty}px`);
    };

    const onLeave = () => {
      this.noHighlight();
      tooltip.style("visibility", "hidden");
    };

    this.onEvent(onMove, onLeave);

    // Draw the legend - pass as selectors?
    const legend = document.querySelector("#legend");
    const template = document.querySelector("#legendTemplate");

    this.legend.forEach((item) => {
      const clone = template.content.cloneNode(true);
      const div = clone.querySelector("div");
      div.dataset.abbrev = item.abbrev;
      div.dataset.hidden = ""; // Initial show/hide state can be set here

      clone.querySelector("rect").setAttribute("fill", item.color);
      clone.querySelector("span").textContent = `${item.name}`;
      legend.appendChild(clone);

      // Create hover events that highlight chart on legend hover, but only if it's active
      // NOTE This creates strange effects on mobile
      // div.addEventListener("mouseover", (elem) => {
      //   if (div.dataset.hidden === "") {
      //     this.highlight(item.abbrev);
      //   } else {
      //     this.noHighlight();
      //   }
      // });

      // Show only that item when double click
      div.addEventListener("dblclick", (evt) => {
        const z = [];
        // TODO Another approach: if the currently double clicked item is the only one
        // shown, then show everything
        if (div.dataset.hidden === "") {
          // Hide everything except the current item
          Array.from(legend.children).forEach((elem) => {
            if (elem.dataset.abbrev === item.abbrev) {
              elem.dataset.hidden = "";
            } else {
              z.push(elem.dataset.abbrev);
              elem.dataset.hidden = true;
            }
          });
        } else {
          // If the item is already hidden, just show everything again
          Array.from(legend.children).forEach((elem) => {
            elem.dataset.hidden = "";
          });
        }
        z.length ? this.hide(...z) : this.showAll();
        evt.preventDefault();
      });

      // Hide the item on single click, unless it is already hidden, then show
      div.addEventListener("click", (evt) => {
        if (div.dataset.hidden === "") {
          this.hide(item.abbrev);
          div.dataset.hidden = "true";
        } else {
          this.show(item.abbrev);
          div.dataset.hidden = "";
        }
      });
    });
  }
}

export function Rates(data) {
  return new RatesChart(data);
}
